import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link as RouterLink } from "react-router-dom";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { BrandLockup } from "../components/Brand";
import { ColorModeToggle } from "../components/ColorModeToggle";
import { VersionLine } from "../components/VersionLine";
import { loadMsg91Widget, WidgetError, type Msg91Widget } from "../auth/msg91Widget";
import { config } from "../config";
import { isTestCodeRequest, maskMobile, normalizeIndianMobile, testCodeAccessToken } from "../api/login";
import { isTestMobile } from "../domain/testMobile";
import { EMPTY_SIGNUP_FORM, checkSignupUsername, sendSignupTestCode, usernameProblem, signUp, signupFormProblems, type SignupForm, type UsernameCheck } from "../api/register";
import { RESUME_NOTICE } from "../api/applicantOnboarding";
import { HumanCheck } from "../components/HumanCheck";
import { PasswordField } from "../components/PasswordField";

/** Seconds before the widget allows another send (MSG91 answered 704 before ~33 s). */
const RESEND_AFTER = 35;
const BUSY_RETRY = 60;

type Step = "details" | "code" | "done";

type Notice =
  | { kind: "problems"; problems: string[] }
  | { kind: "busy"; retryAfter: number }
  | { kind: "widget_error"; message: string }
  | { kind: "invalid_code" }
  | { kind: "resent"; channel: "sms" | "voice" }
  | { kind: "not_verified" }
  | { kind: "exists" }
  | { kind: "username_taken" }
  | { kind: "partial"; message: string }
  | { kind: "unavailable" };

function widgetNotice(err: unknown): Notice {
  if (err instanceof WidgetError && err.throttled) return { kind: "busy", retryAfter: BUSY_RETRY };
  if (err instanceof WidgetError) return { kind: "widget_error", message: err.message };
  return { kind: "widget_error", message: "The SMS service could not be reached" };
}

/**
 * Provider sign-up: mobile first (owner plan 2026-09-13).
 *
 * Details, then the MSG91 code, then the account. The widget proves the mobile
 * in the browser; radaccess confirms that proof with MSG91 before it creates
 * the login (src/api/register.ts). The long application -- PAN, GSTIN, bank,
 * licence -- comes after the provider has signed in.
 *
 * ⛔ PUBLIC. No identity is read and no token is sent.
 */
const USERNAME_CHECK_DELAY_MS = 500;

export function Register() {
  const widgetCfg = config().msg91;
  // ?mobile= prefills the number (assisted onboarding opens /join for the
  // owner on the staff screen; the owner still verifies it themselves).
  const [form, setForm] = useState<SignupForm>(() => {
    const m = new URLSearchParams(window.location.search).get("mobile")?.replace(/\D/g, "").slice(-10) ?? "";
    return { ...EMPTY_SIGNUP_FORM, mobile: m };
  });
  const [step, setStep] = useState<Step>("details");
  const [code, setCode] = useState("");
  const [reqId, setReqId] = useState("");
  const [masked, setMasked] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [created, setCreated] = useState<{ username: string; note: string } | null>(null);
  const [resendUntil, setResendUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const resendIn = Math.max(0, Math.ceil((resendUntil - now) / 1000));
  const widgetRef = useRef<Msg91Widget | null>(null);
  // A verified token is single-use at radaccess, but a THROTTLED check never
  // looked at it, so after "busy" the same token is retried instead of asking
  // the person for another code.
  const tokenRef = useRef<string>("");
  const codeRef = useRef<HTMLInputElement>(null);

  const setResendIn = (seconds: number) => {
    const t = Date.now();
    setNow(t);
    setResendUntil(t + seconds * 1000);
  };

  // Counted against a deadline, not decremented per tick, so a throttled
  // background tab still shows the right number when it wakes.
  useEffect(() => {
    if (resendUntil <= now) return;
    const t = window.setTimeout(() => setNow(Date.now()), 1000);
    return () => window.clearTimeout(t);
  }, [resendUntil, now]);

  // Start the widget as the page opens, not on Send: it fetches its own
  // settings (including the captcha) after it starts, and calling send before
  // they arrive fails with "Invalid Captcha Token".
  useEffect(() => {
    if (!widgetCfg) return;
    loadMsg91Widget(widgetCfg)
      .then((w) => {
        widgetRef.current = w;
      })
      .catch(() => undefined);
  }, [widgetCfg]);

  useEffect(() => {
    if (step === "code") codeRef.current?.focus();
  }, [step]);

  const set = <K extends keyof SignupForm>(k: K, v: SignupForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  // The username is checked as it is typed, so a taken one is marked at once.
  const [nameCheck, setNameCheck] = useState<{ name: string; result: UsernameCheck } | null>(null);
  useEffect(() => {
    const name = form.username.trim();
    if (name === "" || usernameProblem(name) !== "") return;
    let stale = false;
    const t = window.setTimeout(() => {
      void checkSignupUsername(name).then((result) => {
        if (!stale) setNameCheck({ name, result });
      });
    }, USERNAME_CHECK_DELAY_MS);
    return () => {
      stale = true;
      window.clearTimeout(t);
    };
  }, [form.username]);
  const typedName = form.username.trim();
  const nameResult = nameCheck?.name === typedName ? nameCheck.result : null;
  const nameTaken = nameResult?.kind === "taken";

  async function run(action: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  }

  const backToDetails = () => {
    tokenRef.current = "";
    setReqId("");
    setCode("");
    setStep("details");
  };

  const onDetailsSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (!widgetCfg) return;
      const problems = signupFormProblems(form);
      if (problems.length > 0) {
        setNotice({ kind: "problems", problems });
        return;
      }
      const e164 = normalizeIndianMobile(form.mobile);
      if (e164 === null) return;
      // A taken username is refused here, before any code is sent.
      const name = await checkSignupUsername(form.username);
      if (name.kind !== "available") {
        if (name.kind === "taken") setNotice({ kind: "username_taken" });
        else if (name.kind === "invalid") setNotice({ kind: "problems", problems: [name.message] });
        else setNotice({ kind: "unavailable" });
        return;
      }
      // A test number gets its code in the io.ankhos SMS log, not by SMS.
      if (isTestMobile(e164.slice(3))) {
        const t = await sendSignupTestCode(e164);
        if (t.kind === "sent") {
          tokenRef.current = "";
          setReqId(t.requestId);
          setMasked(maskMobile(e164));
          setResendIn(t.resendIn);
          setCode("");
          setStep("code");
        } else {
          setNotice({ kind: "widget_error", message: t.message });
        }
        return;
      }
      try {
        const widget = await loadMsg91Widget(widgetCfg);
        widgetRef.current = widget;
        const id = await widget.send(e164);
        tokenRef.current = "";
        setReqId(id);
        setMasked(maskMobile(e164));
        setResendIn(RESEND_AFTER);
        setCode("");
        setStep("code");
      } catch (err) {
        setNotice(widgetNotice(err));
      }
    });
  };

  const resend = (channel: "sms" | "voice") =>
    run(async () => {
      if (isTestCodeRequest(reqId)) {
        const t = await sendSignupTestCode(form.mobile);
        if (t.kind === "sent") {
          setReqId(t.requestId);
          setResendIn(t.resendIn);
          setCode("");
          setNotice({ kind: "resent", channel: "sms" });
        } else {
          setNotice({ kind: "widget_error", message: t.message });
        }
        return;
      }
      const widget = widgetRef.current;
      if (!widget || !reqId) {
        backToDetails();
        return;
      }
      try {
        await widget.resend(channel, reqId);
        setResendIn(RESEND_AFTER);
        setNotice({ kind: "resent", channel });
      } catch (err) {
        setNotice(widgetNotice(err));
      }
    });

  const onCodeSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const widget = widgetRef.current;
      const testCode = isTestCodeRequest(reqId);
      if (!reqId || (!widget && !testCode)) {
        backToDetails();
        return;
      }
      let token = tokenRef.current;
      if (!token && testCode) {
        token = testCodeAccessToken(reqId, code);
      } else if (!token && widget) {
        try {
          token = await widget.verify(code.trim(), reqId);
        } catch (err) {
          if (err instanceof WidgetError && err.throttled) {
            setNotice({ kind: "busy", retryAfter: BUSY_RETRY });
          } else {
            setNotice({ kind: "invalid_code" });
            setCode("");
          }
          return;
        }
        tokenRef.current = token;
      }

      const r = await signUp({
        firstName: form.firstName,
        lastName: form.lastName,
        mobile: form.mobile,
        username: form.username,
        password: form.password,
        widgetToken: token,
      });
      switch (r.kind) {
        case "created":
          tokenRef.current = "";
          setCreated({ username: r.username, note: r.note });
          setForm(EMPTY_SIGNUP_FORM);
          setStep("done");
          return;
        case "busy":
          setNotice({ kind: "busy", retryAfter: r.retryAfter });
          return;
        case "incomplete":
          setNotice({ kind: "problems", problems: r.problems });
          backToDetails();
          return;
        case "not_verified":
          setNotice({ kind: "not_verified" });
          backToDetails();
          return;
        case "exists":
          setNotice({ kind: "exists" });
          backToDetails();
          return;
        case "username_taken":
          setNotice({ kind: "username_taken" });
          backToDetails();
          return;
        case "partial":
          tokenRef.current = "";
          setNotice({ kind: "partial", message: r.message });
          return;
        case "unavailable":
          tokenRef.current = "";
          setNotice({ kind: "unavailable" });
          return;
      }
    });
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <BrandLockup to="/welcome" />
          <ColorModeToggle />
          <Button size="small" component={RouterLink} to="/welcome">
            Back
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Create your provider account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Verify your mobile number, and you can sign in straight away with a code we text you (a password is
          optional). Your PAN,
          GSTIN, bank and licence details come next; the account stays <strong>unapproved</strong> until we
          have verified them.
        </Typography>

        {!widgetCfg && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Sign-up is not available right now</AlertTitle>
            Mobile verification is not set up on this site. Please try again later.
          </Alert>
        )}

        {notice && <NoticeAlert notice={notice} />}

        {step === "details" && widgetCfg && (
          <Paper sx={{ p: 3 }}>
            <Box component="form" noValidate onSubmit={onDetailsSubmit}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="First name"
                    fullWidth
                    required
                    autoComplete="given-name"
                    value={form.firstName}
                    onChange={(e) => set("firstName", e.target.value)}
                    slotProps={{ htmlInput: { "data-testid": "first-name" } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Last name"
                    fullWidth
                    required
                    autoComplete="family-name"
                    value={form.lastName}
                    onChange={(e) => set("lastName", e.target.value)}
                    slotProps={{ htmlInput: { "data-testid": "last-name" } }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Mobile number"
                    fullWidth
                    required
                    autoComplete="tel-national"
                    value={form.mobile}
                    onChange={(e) => set("mobile", e.target.value)}
                    helperText="You sign in with this number."
                    slotProps={{
                      input: { startAdornment: <InputAdornment position="start">+91</InputAdornment> },
                      htmlInput: { inputMode: "tel", "data-testid": "mobile" },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Username"
                    fullWidth
                    required
                    autoComplete="username"
                    value={form.username}
                    onChange={(e) => set("username", e.target.value)}
                    error={nameTaken}
                    helperText={
                      nameTaken
                        ? "This username is already taken. Choose another."
                        : nameResult?.kind === "available"
                          ? "✓ This username is available. You can sign in with this or your mobile number."
                          : "You can sign in with this or your mobile number."
                    }
                    slotProps={{ htmlInput: { "data-testid": "username" } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <PasswordField
                    label="Password"
                    required
                    fullWidth
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                    helperText="At least 8 characters, with upper and lower case, a number and a symbol."
                    slotProps={{ htmlInput: { "data-testid": "password" } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <PasswordField
                    label="Confirm password"
                    fullWidth
                    autoComplete="new-password"
                    value={form.confirm}
                    onChange={(e) => set("confirm", e.target.value)}
                    slotProps={{ htmlInput: { "data-testid": "confirm" } }}
                  />
                </Grid>
              </Grid>
              <Button type="submit" variant="contained" disabled={busy || nameTaken} sx={{ mt: 3 }} data-testid="send-code">
                {busy ? "Sending…" : "Send code"}
              </Button>
            </Box>
          </Paper>
        )}

        {step === "code" && (
          <Paper sx={{ p: 3 }}>
            <Box component="form" noValidate onSubmit={onCodeSubmit}>
              <Typography sx={{ mb: 2 }}>
                {isTestCodeRequest(reqId)
                  ? "Test number: no SMS is sent. Read the code in io.ankhos's Reports › SMS log."
                  : `Enter the code we sent to ${masked}.`}
              </Typography>
              <TextField
                label="Code"
                fullWidth
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputRef={codeRef}
                slotProps={{ htmlInput: { inputMode: "numeric", "data-testid": "otp-code" } }}
              />
              <Box sx={{ mt: 2 }}>
                <HumanCheck action="signup" />
              </Box>
              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }} useFlexGap>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={busy || (!code.trim() && !tokenRef.current)}
                  data-testid="create-account"
                >
                  {busy ? "Creating…" : "Create account"}
                </Button>
                <Button size="small" onClick={() => void resend("sms")} disabled={busy || resendIn > 0}>
                  {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
                </Button>
                <Button size="small" onClick={() => void resend("voice")} disabled={busy || resendIn > 0}>
                  Call me instead
                </Button>
                <Button size="small" onClick={backToDetails} disabled={busy}>
                  Change details
                </Button>
              </Stack>
            </Box>
          </Paper>
        )}

        {step === "done" && created && (
          <Alert severity="success" data-testid="signup-created">
            <AlertTitle>Your account is ready</AlertTitle>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {created.note || "Sign in with your mobile number and password."} You sign in as{" "}
              <strong>{created.username}</strong>.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }} data-testid="signup-resume-notice">
              {RESUME_NOTICE}
            </Typography>
            <Button variant="contained" component={RouterLink} to="/">
              Sign in
            </Button>
          </Alert>
        )}

        {step !== "done" && (
          <Typography variant="body2" sx={{ mt: 3 }}>
            Already have an account?{" "}
            <Link component={RouterLink} to="/">
              Sign in
            </Link>
          </Typography>
        )}

        <Box sx={{ mt: 4 }}>
          <VersionLine />
        </Box>
      </Container>
    </Box>
  );
}

function NoticeAlert({ notice }: { notice: Notice }) {
  switch (notice.kind) {
    case "problems":
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Please check these</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {notice.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </Box>
        </Alert>
      );
    case "busy":
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          Mobile verification is busy. Wait about {notice.retryAfter} seconds and try again.
        </Alert>
      );
    case "widget_error":
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {notice.message}
        </Alert>
      );
    case "invalid_code":
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          That code is not right. Check it and try again.
        </Alert>
      );
    case "resent":
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          {notice.channel === "voice" ? "You will get a call with the code." : "A new code is on its way."}
        </Alert>
      );
    case "not_verified":
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          Your mobile number could not be verified. Send a new code and try again.
        </Alert>
      );
    case "username_taken":
      return (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="username-taken">
          This username is already taken. Choose another; you will verify your mobile number again.
        </Alert>
      );
    case "exists":
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>You already have an account</AlertTitle>
          This mobile number is already registered.{" "}
          <Link component={RouterLink} to="/">
            Sign in instead
          </Link>
          .
        </Alert>
      );
    case "partial":
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Your account was only partly created</AlertTitle>
          {notice.message}. Please do not sign up again.
        </Alert>
      );
    case "unavailable":
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Nothing was created</AlertTitle>
          The sign-up service could not be reached. Please try again in a few minutes.
        </Alert>
      );
  }
}
